"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Check, Globe } from "lucide-react";
import Link from "next/link";

type Stage = {
  label: { en: string; es: string };
  at: number; // seconds when complete
};

const STAGES: Stage[] = [
  { label: { en: "Pull your menu", es: "Importa tu menú" }, at: 8 },
  { label: { en: "Build hero + brand", es: "Construir hero + marca" }, at: 18 },
  { label: { en: "Generate pages", es: "Generar páginas" }, at: 28 },
  { label: { en: "Optimize for Google", es: "Optimizar para Google" }, at: 36 },
  { label: { en: "Deploy", es: "Deploy" }, at: 43 },
];

const TOTAL = 43;

export default function WebsiteHero() {
  const { t } = useLang();
  const [t_sec, setT] = useState(0);

  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const dur = 5500; // animate the full 43s in 5.5s
    const tick = (now: number) => {
      const tt = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - tt, 2.2);
      setT(TOTAL * eased);
      if (tt < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const pct = (t_sec / TOTAL) * 100;
  const stagesCompleted = STAGES.filter((s) => t_sec >= s.at).length;

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-instant-website.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Instant website · Auto from your menu", "Sitio web instantáneo · Auto desde tu menú")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Seconds", "Segundos")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("to a website.", "para un sitio web.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "No designer. No domain shopping. No template wrestle. Type your restaurant name, your menu is already in the POS, and the public site is up. Mobile-first, Google-friendly, yours.",
            "Sin diseñador. Sin shopping de dominio. Sin pelearte con un template. Escribes el nombre, tu menú ya está en el POS, y el sitio público está arriba. Mobile-first, friendly para Google, tuyo."
          )}
        </motion.p>

        {/* Countdown showcase */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[920px] mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.06)] mb-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* Counter */}
            <div className="md:col-span-5 text-center md:text-left">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                {t("Build progress · illustration", "Progreso · ilustración")}
              </div>
              <div className="flex items-baseline gap-2 justify-center md:justify-start">
                <div
                  className="text-[80px] md:text-[100px] font-black leading-none tabular-nums"
                  style={{
                    background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {t_sec >= TOTAL ? "00" : Math.floor(t_sec).toString().padStart(2, "0")}
                </div>
                <div
                  className="text-[28px] font-black tracking-tight tabular-nums"
                  style={{ color: "#D7263D" }}
                >
                  {t_sec >= TOTAL ? "00" : Math.floor((t_sec * 100) % 100).toString().padStart(2, "0")}
                </div>
                <div className="text-[13px] font-bold uppercase tracking-widest text-redy-secondary/60 mb-3">
                  s
                </div>
              </div>
              <div className="text-[14px] font-bold text-redy-charcoal mt-3">
                {t_sec >= TOTAL ? (
                  <span style={{ color: "#2ECC71" }}>
                    ✓ {t("Live · pepitos.useredy.com", "Vivo · pepitos.useredy.com")}
                  </span>
                ) : (
                  <span>
                    {t("Building your site…", "Construyendo tu sitio…")}
                  </span>
                )}
              </div>
            </div>

            {/* Stages */}
            <div className="md:col-span-7">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-4">
                {t("Stages", "Etapas")} · {stagesCompleted}/{STAGES.length}
              </div>

              {/* Progress bar */}
              <div className="h-1.5 bg-redy-warm-white rounded-full overflow-hidden mb-6">
                <motion.div
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.1 }}
                  className="h-full rounded-full"
                  style={{ background: "linear-gradient(90deg, #F4722B 0%, #D7263D 100%)" }}
                />
              </div>

              {/* Stage list */}
              <div className="space-y-2">
                {STAGES.map((stage, i) => {
                  const done = t_sec >= stage.at;
                  const active = !done && (i === 0 || t_sec >= (STAGES[i - 1]?.at ?? 0));
                  return (
                    <div
                      key={stage.label.en}
                      className="flex items-center gap-3 text-[12px]"
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: done
                            ? "#2ECC71"
                            : active
                              ? "rgba(244,114,43,0.20)"
                              : "rgba(0,0,0,0.05)",
                        }}
                      >
                        {done ? (
                          <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                        ) : active ? (
                          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#F4722B" }} />
                        ) : null}
                      </div>
                      <div
                        className={`flex-1 font-bold ${
                          done
                            ? "text-redy-charcoal"
                            : active
                              ? "text-redy-charcoal"
                              : "text-redy-secondary/45"
                        }`}
                      >
                        {t(stage.label.en, stage.label.es)}
                      </div>
                      <div className="text-[11px] tabular-nums text-redy-secondary/55">
                        {stage.at}s
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Generate my site →", "Genera mi sitio →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <div className="mt-4 text-[12px] font-medium text-redy-secondary/60 flex items-center justify-center gap-1.5">
            <Globe className="w-3 h-3" />
            <span>{t("Free on yourname.useredy.com · Custom domain on Plus and Pro", "Gratis en tunombre.useredy.com · Dominio custom en Plus y Pro")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
